/**
 * Admin tab — Paiements
 *
 * Liste filtrable des relevés mensuels de tous les apprenants éligibles.
 * Actions: Marquer comme payé (avec référence + méthode + notes) | Rejeter (avec motif).
 *
 * Architecture défensive :
 *  - Reload automatique après chaque action
 *  - Modale dédiée pour la saisie (référence obligatoire) → évite les "click sans contexte"
 *  - Filtre par statut côté serveur (paramètre query)
 *  - Compteurs visibles pour priorisation visuelle (X claimed, Y paid)
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Wallet,
    CheckCircle2,
    XCircle,
    Clock,
    Send,
    Receipt,
    Filter,
    Loader2,
    PlayCircle,
    AlertTriangle,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import ThemedSelect from "../../components/ThemedSelect";

const STATUS_TABS = [
    { id: "all", label: "Tous" },
    { id: "claimed", label: "À traiter" },  // = "claimed" = highest-priority
    { id: "pending", label: "En attente" },
    { id: "paid", label: "Payés" },
    { id: "rejected", label: "Rejetés" },
];

const STATUS_BADGE = {
    pending: { label: "En attente", cls: "bg-white/5 text-white/65 border-white/10", icon: Clock },
    claimed: { label: "À traiter", cls: "bg-shariti-yellow/15 text-shariti-yellow border-shariti-yellow/30", icon: Send },
    paid: { label: "Payé", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: CheckCircle2 },
    rejected: { label: "Rejeté", cls: "bg-red-500/15 text-red-300 border-red-500/30", icon: XCircle },
};

const PAYMENT_METHODS = [
    { value: "transfer", label: "Virement bancaire" },
    { value: "payslip", label: "Fiche de paie (variable)" },
    { value: "invoice", label: "Facture (auto-entrepreneur)" },
    { value: "cash", label: "Espèces" },
    { value: "other", label: "Autre" },
];

function formatEUR(n) {
    return Number(n || 0).toLocaleString("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
    });
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

function StatusPill({ status }) {
    const m = STATUS_BADGE[status] || STATUS_BADGE.pending;
    const Icon = m.icon;
    return (
        <span
            className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full border ${m.cls}`}
        >
            <Icon className="w-3 h-3" />
            {m.label}
        </span>
    );
}

function PayModal({ statement, onClose, onConfirm }) {
    const [ref, setRef] = useState("");
    const [method, setMethod] = useState("transfer");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!ref.trim()) {
            toast.error("La référence est obligatoire (n° fiche de paie / facture / virement).");
            return;
        }
        setBusy(true);
        try {
            await onConfirm({
                payment_reference: ref.trim(),
                payment_method: method,
                admin_notes: notes.trim(),
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
                onClick={onClose}
                data-testid="payout-pay-modal"
            >
                <motion.div
                    initial={{ scale: 0.96, y: 8 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.96, y: 8 }}
                    className="bg-shariti-dark border border-white/10 rounded-3xl p-6 w-full max-w-md"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center gap-2 mb-1">
                        <Receipt className="w-5 h-5 text-emerald-400" />
                        <h3 className="text-lg font-extrabold text-white">
                            Marquer comme payé
                        </h3>
                    </div>
                    <p className="text-xs text-white/55 mb-4 leading-relaxed">
                        {statement.user_name} · {statement.period_label} ·{" "}
                        <span className="text-white">{formatEUR(statement.gross_amount_eur)}</span>
                    </p>

                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                Référence <span className="text-shariti-orange">*</span>
                            </label>
                            <input
                                value={ref}
                                onChange={(e) => setRef(e.target.value)}
                                placeholder="Ex : VIR-2026-042 ou Fiche de paie mai 2026"
                                data-testid="pay-modal-reference"
                                autoFocus
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                Méthode
                            </label>
                            <ThemedSelect
                                value={method}
                                onChange={setMethod}
                                options={PAYMENT_METHODS.map(m => ({ value: m.value, label: m.label }))}
                                dataTestid="pay-modal-method"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                                Notes internes (optionnel)
                            </label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={2}
                                placeholder="Pour la traçabilité interne…"
                                data-testid="pay-modal-notes"
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/30 outline-none focus:border-shariti-orange/60"
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 mt-5">
                        <button
                            onClick={onClose}
                            disabled={busy}
                            className="flex-1 py-2.5 rounded-xl border border-white/10 text-white/70 hover:bg-white/5"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={submit}
                            disabled={busy}
                            data-testid="pay-modal-confirm"
                            className="flex-1 py-2.5 rounded-xl bg-emerald-500 text-white font-bold hover:bg-emerald-400 inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {busy ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" /> En cours…
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="w-4 h-4" /> Valider
                                </>
                            )}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

function RejectModal({ statement, onClose, onConfirm }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!reason.trim()) {
            toast.error("Le motif est obligatoire.");
            return;
        }
        setBusy(true);
        try {
            await onConfirm(reason.trim());
        } finally {
            setBusy(false);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
                onClick={onClose}
                data-testid="payout-reject-modal"
            >
                <motion.div
                    initial={{ scale: 0.96, y: 8 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.96, y: 8 }}
                    className="bg-shariti-dark border border-white/10 rounded-3xl p-6 w-full max-w-md"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center gap-2 mb-1">
                        <XCircle className="w-5 h-5 text-red-400" />
                        <h3 className="text-lg font-extrabold text-white">Rejeter cette demande</h3>
                    </div>
                    <p className="text-xs text-white/55 mb-4 leading-relaxed">
                        {statement.user_name} · {statement.period_label} ·{" "}
                        {formatEUR(statement.gross_amount_eur)}
                    </p>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={4}
                        placeholder="Motif visible par l'apprenant"
                        autoFocus
                        data-testid="reject-modal-reason"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-white/30 outline-none focus:border-red-500/60"
                    />
                    <div className="flex gap-2 mt-4">
                        <button
                            onClick={onClose}
                            disabled={busy}
                            className="flex-1 py-2.5 rounded-xl border border-white/10 text-white/70 hover:bg-white/5"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={submit}
                            disabled={busy}
                            data-testid="reject-modal-confirm"
                            className="flex-1 py-2.5 rounded-xl bg-red-500/80 text-white font-bold hover:bg-red-500 inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                            Rejeter
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

export default function AdminPayouts() {
    const [data, setData] = useState({ items: [], counts: {} });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("claimed");
    const [payTarget, setPayTarget] = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [generating, setGenerating] = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const params = filter === "all" ? {} : { status: filter };
            const r = await api.get("/admin/payouts", { params });
            setData(r.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
    }, [filter]);

    const handlePay = async (payload) => {
        try {
            await api.post(`/admin/payouts/${payTarget.statement_id}/mark-paid`, payload);
            toast.success("Paiement enregistré ✓");
            setPayTarget(null);
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const handleReject = async (reason) => {
        try {
            await api.post(`/admin/payouts/${rejectTarget.statement_id}/reject`, { reason });
            toast.success("Demande rejetée");
            setRejectTarget(null);
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const r = await api.post("/admin/payouts/generate-monthly");
            toast.success(
                `Relevés ${r.data.period_label} générés (${r.data.users_processed} apprenants)`,
            );
            await reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div data-testid="admin-payouts-tab">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <Wallet className="w-5 h-5 text-shariti-orange" />
                        <h2 className="text-2xl font-extrabold text-white tracking-tight">
                            Paiements
                        </h2>
                    </div>
                    <p className="text-sm text-white/55 leading-relaxed">
                        Tu vois ici les relevés mensuels demandés par les apprenants éligibles.
                        Marque-les comme payés avec une référence pour la traçabilité.
                    </p>
                </div>
                <button
                    onClick={handleGenerate}
                    disabled={generating}
                    data-testid="admin-generate-monthly-btn"
                    className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:border-shariti-orange/40 text-white font-bold px-3.5 py-2 rounded-xl text-sm transition-all"
                    title="Génère les relevés du mois précédent pour tous les apprenants éligibles"
                >
                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                    {generating ? "En cours…" : "Générer le mois précédent"}
                </button>
            </div>

            <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
                <Filter className="w-4 h-4 text-white/40 shrink-0" />
                {STATUS_TABS.map((t) => {
                    const active = filter === t.id;
                    const count = t.id === "all" ? null : data.counts?.[t.id];
                    return (
                        <button
                            key={t.id}
                            onClick={() => setFilter(t.id)}
                            data-testid={`admin-payouts-filter-${t.id}`}
                            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-widest transition-all ${
                                active
                                    ? "bg-shariti-orange text-white"
                                    : "bg-white/5 text-white/60 hover:bg-white/10"
                            }`}
                        >
                            {t.label}
                            {count !== null && count > 0 && (
                                <span className="ml-1.5 opacity-70">({count})</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {loading && (
                <div className="text-center text-white/45 text-sm py-8">Chargement…</div>
            )}

            {!loading && data.items.length === 0 && (
                <div
                    className="glass-card rounded-2xl p-6 sm:p-8"
                    data-testid="admin-payouts-empty"
                >
                    <div className="flex items-start gap-3 mb-4">
                        <div className="w-10 h-10 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                            <Wallet className="w-5 h-5 text-shariti-orange" />
                        </div>
                        <div>
                            <p className="font-extrabold text-white tracking-tight">
                                Aucun relevé pour l'instant
                            </p>
                            <p className="text-sm text-white/55 leading-relaxed mt-1">
                                Voici comment des relevés vont apparaître ici :
                            </p>
                        </div>
                    </div>
                    <ol className="space-y-2.5 text-sm text-white/65 leading-relaxed pl-4">
                        <li className="list-decimal">
                            Va dans <span className="text-white">Utilisateurs</span> et clique sur l'icône{" "}
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 align-baseline">
                                <Wallet className="w-3 h-3" /> Wallet
                            </span>{" "}
                            à droite d'un apprenant pour activer la rémunération en €.
                        </li>
                        <li className="list-decimal">
                            L'apprenant verra alors apparaître la page « Mes paiements » dans
                            son menu et son <span className="text-white">relevé du mois courant</span>{" "}
                            (calculé en temps réel depuis ses points).
                        </li>
                        <li className="list-decimal">
                            Au début du mois suivant, son relevé devient « réclamable ». L'apprenant
                            clique sur <span className="text-white">« Réclamer »</span> → le relevé
                            apparaît ici en <span className="text-shariti-yellow">À traiter</span>.
                        </li>
                        <li className="list-decimal">
                            Tu cliques alors sur <span className="text-emerald-300">« Marquer payé »</span>,
                            renseignes la référence (n° fiche de paie / facture / virement) et{" "}
                            <span className="text-white">une trace définitive est conservée</span>.
                        </li>
                        <li className="list-decimal">
                            Tu peux aussi <span className="text-white">forcer la génération du mois précédent</span>{" "}
                            avec le bouton en haut à droite — utile pour figer les relevés en début de mois.
                        </li>
                    </ol>
                    <div className="mt-5 pt-4 border-t border-white/5 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-shariti-yellow shrink-0 mt-0.5" />
                        <p className="text-xs text-white/55 leading-relaxed">
                            Le taux de conversion (1 pt = X €) est défini dans{" "}
                            <span className="text-white">Récompenses</span>. Tu peux activer
                            globalement la conversion en € (toggle{" "}
                            <span className="text-white">cash_enabled</span>) une fois que tu es
                            prêt à entrer en phase pilote.
                        </p>
                    </div>
                </div>
            )}

            <div className="space-y-2.5">
                {!loading &&
                    data.items.map((s) => (
                        <div
                            key={s.statement_id}
                            className="glass-card rounded-2xl p-4 flex flex-col md:flex-row md:items-center md:gap-4"
                            data-testid={`admin-payout-row-${s.statement_id}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                    <p className="font-extrabold text-white truncate">
                                        {s.user_name}
                                    </p>
                                    {s.user_deleted && (
                                        <span
                                            data-testid={`payout-deleted-badge-${s.statement_id}`}
                                            className="text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30"
                                        >
                                            compte supprimé
                                        </span>
                                    )}
                                    <StatusPill status={s.status} />
                                </div>
                                <p className="text-xs text-white/50 truncate">
                                    {s.user_email}
                                </p>
                            </div>
                            <div className="flex items-center gap-4 mt-2 md:mt-0">
                                <div>
                                    <p className="text-[10px] uppercase tracking-widest text-white/35">
                                        Période
                                    </p>
                                    <p className="text-sm font-bold text-white">{s.period_label}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-widest text-white/35">
                                        Montant
                                    </p>
                                    <p className="text-sm font-extrabold text-shariti-orange-light">
                                        {formatEUR(s.gross_amount_eur)}
                                    </p>
                                    <p className="text-[10px] text-white/40">
                                        {s.points_earned.toLocaleString("fr-FR")} pts
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mt-3 md:mt-0">
                                {s.status !== "paid" && s.status !== "rejected" && (
                                    <>
                                        <button
                                            onClick={() => setPayTarget(s)}
                                            data-testid={`admin-pay-${s.statement_id}`}
                                            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition-all"
                                            disabled={s.gross_amount_eur === 0}
                                            title={
                                                s.gross_amount_eur === 0
                                                    ? "Pas de montant à payer"
                                                    : "Marquer comme payé"
                                            }
                                        >
                                            Marquer payé
                                        </button>
                                        <button
                                            onClick={() => setRejectTarget(s)}
                                            data-testid={`admin-reject-${s.statement_id}`}
                                            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-red-500/15 text-red-300 hover:bg-red-500/25 transition-all"
                                        >
                                            Rejeter
                                        </button>
                                    </>
                                )}
                                {s.status === "paid" && s.payment_reference && (
                                    <code className="text-[10px] bg-emerald-500/10 text-emerald-300 px-2 py-1 rounded font-mono">
                                        {s.payment_reference}
                                    </code>
                                )}
                                {s.status === "paid" && s.paid_at && (
                                    <span className="text-[10px] text-white/40">
                                        {formatDate(s.paid_at)}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
            </div>

            {payTarget && (
                <PayModal
                    statement={payTarget}
                    onClose={() => setPayTarget(null)}
                    onConfirm={handlePay}
                />
            )}
            {rejectTarget && (
                <RejectModal
                    statement={rejectTarget}
                    onClose={() => setRejectTarget(null)}
                    onConfirm={handleReject}
                />
            )}
        </div>
    );
}
